// main.tsx
import App from "@/App";
import "@/index.css";
import { store, persistor } from "@/redux/store"; // Import both store and persistor
import { setupListeners } from "@reduxjs/toolkit/query";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HelmetProvider } from "react-helmet-async";
import * as Sentry from "@sentry/react"; // Import Sentry
import SentryErrorFallback from "./components/Error/SentryErrorFallback";


setupListeners(store.dispatch);

// ── LOCAL DEV ONLY — fake donor-org harness (uncommitted; see client/src/devMock) ──
// Absent-safe: if the devMock folder is missing the glob is empty and this is inert.
// Stripped from production builds by the import.meta.env.DEV guard.
if (import.meta.env.DEV) {
  const devMock = import.meta.glob("./devMock/activate.ts", { eager: true });
  Object.values(devMock).forEach((m) => (m as { activate?: () => void }).activate?.());
}

// ✅ EDITED BY SOHAIL: Load config from public folder and set window variables
// IMPORTANT: Wait for config to load before rendering the app
fetch("/config.json", {
  cache: "no-store",
  headers: {
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  },
})
  .then((res) => {
    if (!res.ok) {
      throw new Error(
        `Failed to load config.json: ${res.status} ${res.statusText}`,
      );
    }

    return res.json();
  })
  .then((config) => {
    window.API_URL = config.API_URL;
    window.WS_URL = config.WS_URL;
    window.FRONTEND_ENVIRONMENT = config.FRONTEND_ENVIRONMENT;

    console.log("Config loaded:", {
      API_URL: window.API_URL,
      WS_URL: window.WS_URL,
      FRONTEND_ENVIRONMENT: window.FRONTEND_ENVIRONMENT,
    });
    // 3. ✅ SENTRY LOGIC
    // We check the DYNAMIC environment variable
    if (window.FRONTEND_ENVIRONMENT === "production") {
      // We grab the STATIC build-time variable
      const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

      if (sentryDsn) {
        Sentry.init({
          dsn: sentryDsn,
          environment: window.FRONTEND_ENVIRONMENT,
          integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
          ],
          // Performance Monitoring
          tracesSampleRate: 0.1, // Capture 10% of transactions
          // Session Replay
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
        });
        console.log("✅ Sentry Initialized (Production Mode)");
      } else {
        console.warn(
          "⚠️ Production environment detected, but VITE_SENTRY_DSN is missing.",
        );
      }
    } else {
      console.log("ℹ️ Sentry skipped (Not production)");
    }

    // 4. Render App
    renderApp();
  })
  .catch((error) => {
    console.error("Failed to load config.json:", error);
    console.error(
      "Cannot start app without config.json - please ensure config.json exists and is accessible",
    );

    console.warn("Using fallback configuration:", {
      API_URL: window.API_URL,
      WS_URL: window.WS_URL,
      FRONTEND_ENVIRONMENT: window.FRONTEND_ENVIRONMENT,
    });

    // Render the app with fallback config
    renderApp();
  });

// ✅ EDITED BY SOHAIL: Wrap render in function to wait for config loading
function renderApp() {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <Provider store={store}>
      {/* PersistGate ensures the persisted state is loaded before rendering the app */}
      <PersistGate loading={null} persistor={persistor}>
        <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
          <HelmetProvider>
            <Sentry.ErrorBoundary
              fallback={<SentryErrorFallback />}
              showDialog={true}
            >
              <App />
            </Sentry.ErrorBoundary>
          </HelmetProvider>
        </GoogleOAuthProvider>
      </PersistGate>
    </Provider>,
  );
}
